import { create } from 'zustand';
import { supabase } from '../config/supabase';
import { User } from '@supabase/supabase-js';
import { mockTags } from '../data/mock-data';
import { useFileStore } from './file-store';
import { useCategoryStore } from './category-store';
import { ensureFSRegistration } from '../services/app-registration.service';
import type { TenantId } from '../config/tenant';

interface SignUpResult {
  email: string;
  needsEmailConfirmation: boolean;
  redirectTo: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  tagsSeeded: boolean;
  lastFetch: number;

  // Actions
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, tenant?: TenantId) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  seedTags: () => Promise<void>;
  resetPassword: (email: string, redirectUrl?: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  signInWithOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: false,
  error: null,
  lastFetch: 0,
  tagsSeeded: false,

  seedTags: async () => {
    try {
      const state = useAuthStore.getState();
      const now = Date.now();
      if (state.tagsSeeded && now - state.lastFetch < 300000) return; // 5 minutes cache

      const { data: existingTags } = await supabase
        .from('tags')
        .select('id')
        .limit(1);

      if (!existingTags?.length) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        await supabase
          .from('tags')
          .insert(
            mockTags.map(tag => ({
              id: tag.id,
              name: tag.name,
              color: tag.color,
              created_by: user.id
            }))
          );
      }
      set({ tagsSeeded: true, lastFetch: now });
    } catch (error) {
      console.error('Error seeding tags:', error);
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      set({ loading: true, error: null });
      if (get().user) {
        await supabase.auth.signOut();
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (!data?.user || !data?.session) {
        throw new Error('Login failed: Unable to create session');
      }

      set({ user: data.user });

      // 文件系统资料是登录后的补充数据，不应该阻塞认证和页面跳转。
      // Data API 偶发超时时，会话仍然已经有效，后台检查失败只记日志。
      void ensureFSRegistration(data.user.id)
        .then(({ isNewUser }) => {
          if (isNewUser) {
            console.log('🆕 欢迎新用户首次访问文件系统！');
          }
        })
        .catch((regError) => {
          console.error('文件系统注册检查失败:', regError);
        });
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signUp: async (email: string, password: string, tenant?: TenantId) => {
    try {
      set({ loading: true, error: null });
      console.log('开始注册流程...');

      const pathTenant = window.location.pathname.split('/')[1];
      const resolvedTenant: TenantId = tenant ?? (pathTenant === 'intl' ? 'intl' : 'cn');
      const emailRedirectTo = `${window.location.origin}/${resolvedTenant}/portal`;

      // 尝试注册
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo,
          data: {
            email,
            tenant: resolvedTenant,
          }
        }
      });

      if (error) {
        console.error('Supabase注册错误:', {
          error,
          code: error.status,
          message: error.message
        });

        // 处理特定错误
        if (error.message.includes('User already registered')) {
          throw new Error('该邮箱已被注册');
        }

        throw new Error('无法创建账户，请稍后再试');
      }

      if (!data.user) {
        console.error('注册未返回用户数据');
        throw new Error('注册失败: 无法创建用户账户');
      }

      console.log('用户创建成功:', data.user.id);

      if (!data.session) {
        set({ user: null });
        return {
          email,
          needsEmailConfirmation: true,
          redirectTo: emailRedirectTo,
        };
      }

      // 设置用户状态
      set({ user: data.user });

      // 初始化用户数据
      try {
        await Promise.all([
          useAuthStore.getState().seedTags(),
          useFileStore.getState().fetchFiles(),
          useCategoryStore.getState().fetchCategories()
        ]);
        console.log('用户数据初始化成功');
      } catch (initError) {
        console.error('初始化用户数据错误:', {
          error: initError,
          userId: data.user.id
        });
        // 不抛出错误，让用户能够继续，日后可重试加载数据
      }

      return {
        email,
        needsEmailConfirmation: false,
        redirectTo: emailRedirectTo,
      };
    } catch (error) {
      console.error('注册流程错误:', {
        error,
        message: error instanceof Error ? error.message : '未知错误'
      });

      const message = error instanceof Error ? error.message : '注册失败';
      set({ error: message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signOut: async () => {
    try {
      set({ loading: true, error: null });
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // 确保清理状态
      set({
        user: null,
        tagsSeeded: false,
        lastFetch: 0
      });

      // 清理其他存储
      try {
        useFileStore.getState().clearFiles();
        useCategoryStore.getState().clearCategories();
      } catch (error) {
        console.error('清理存储错误:', error);
      }
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  resetPassword: async (email: string, redirectUrl?: string) => {
    try {
      set({ loading: true, error: null });
      const tenant = window.location.pathname.split('/')[1] || 'cn';
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl || `${window.location.origin}/${tenant}/reset-password`
      });
      if (error) throw error;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updatePassword: async (newPassword: string) => {
    try {
      set({ loading: true, error: null });
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  signInWithOtp: async (email: string) => {
    try {
      set({ loading: true, error: null });
      const tenant = window.location.pathname.split('/')[1] || 'cn';
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: `${window.location.origin}/${tenant}/portal`
        }
      });
      if (error) throw error;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  verifyOtp: async (email: string, token: string) => {
    try {
      set({ loading: true, error: null });
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });
      if (error) throw error;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  checkAuth: async () => {
    try {
      set({ loading: true, error: null });

      // 首先尝试获取当前会话
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('获取会话错误:', sessionError);
        set({ user: null, loading: false });
        return;
      }

      if (session?.user) {
        console.log('找到有效会话，用户:', session.user.email);
        set({ user: session.user, loading: false });

        // 异步加载数据，不阻塞认证流程
        setTimeout(async () => {
          try {
            await Promise.all([
              useAuthStore.getState().seedTags(),
              useCategoryStore.getState().fetchCategories(),
              useFileStore.getState().fetchFiles()
            ]);
          } catch (error) {
            console.error('加载初始数据错误:', error);
          }
        }, 100);
      } else {
        console.log('未找到有效会话');
        set({ user: null, loading: false });
      }

      // 设置认证状态变化监听器（只设置一次）
      if (!(window as any).authStateListenerSetup) {
        (window as any).authStateListenerSetup = true;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('认证状态变化:', event, session?.user?.email);

          if (event === 'SIGNED_IN' && session?.user) {
            set({ user: session.user });
            // 延迟加载数据确保认证状态就绪
            setTimeout(async () => {
              try {
                await Promise.all([
                  useAuthStore.getState().seedTags(),
                  useCategoryStore.getState().fetchCategories(),
                  useFileStore.getState().fetchFiles()
                ]);
              } catch (error) {
                console.error('登录后加载数据错误:', error);
              }
            }, 100);
          } else if (event === 'SIGNED_OUT') {
            set({ user: null, tagsSeeded: false, lastFetch: 0 });
            // 清理其他存储
            try {
              useFileStore.getState().clearFiles();
              useCategoryStore.getState().clearCategories();
            } catch (error) {
              console.error('登出后清理数据错误:', error);
            }
          } else if (event === 'PASSWORD_RECOVERY' && session?.user) {
            set({ user: session.user });
            // 密码恢复事件：用户从邮件链接回来，自动登录状态
            console.log('密码恢复模式：用户已通过邮件验证');
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            set({ user: session.user });
          }
        });

        // 页面卸载时清理监听器
        window.addEventListener('beforeunload', () => {
          subscription.unsubscribe();
        });
      }

    } catch (error) {
      console.error('认证检查错误:', error);
      set({ error: (error as Error).message, user: null, loading: false });
    }
  }
}));
